/*
 * Copyright © 2016-2020 The Thingsboard Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import './silo-group-dashboard-widget.scss';

/* eslint-disable import/no-unresolved, import/default */

import siloGroupDashboardWidgetTemplate from './silo-group-dashboard-widget.tpl.html';

/* eslint-enable import/no-unresolved, import/default */

export default angular.module('agrolog.client.siloGroupDashboardWidget', [])
    .directive('agSiloGroupDashboardWidget', SiloGroupDashboardWidget)
    .name;

/*@ngInject*/
function SiloGroupDashboardWidget() {
    return {
        restrict: "E",
        scope: true,
        bindToController: {
            ctx: '=',
            siloGroupId: '='
        },
        controller: SiloGroupDashboardWidgetController,
        controllerAs: 'vm',
        templateUrl: siloGroupDashboardWidgetTemplate
    };
}

/*@ngInject*/
function SiloGroupDashboardWidgetController($scope, $log, $translate, unitService, siloGroupService, agTypes, types) {

    let vm = this;

    vm.loading = true;
    vm.agTypes = agTypes;

    vm.tempHistoryUnits = unitService.temperatureUnitSymbol();

    vm.latestValuesSubscriptionOptions = {
        callbacks: {
            onDataUpdated: onDataUpdated,
            onDataUpdateError: onDataUpdateError,
            dataLoading: function(subscription) {
                vm.loading = subscription.loadingData;
            }
        },
        useDashboardTimewindow: false
    };

    vm.flotSettings = {
        shadowSize: 0,
        yaxis: {
            showLabels: false
        },
        xaxis: {
            showLabels: false
        },
        grid: {
            outlineWidth: 0,
            margin: 0,
            minBorderMargin: 0,
            tickColor: 'rgba(255, 255, 255, 0)' // Workaround to hide grid lines
        }
    };

    vm.flotKeySettings = {
        lineWidth: 1
    };

    vm.tempColor = tempColor;
    vm.deltaTempColor = deltaTempColor;

    $scope.$watch('vm.siloGroupId', function() {
        vm.siloGroup = null;
        reset();
        if (vm.siloGroupId) {
            vm.loading = true;
            siloGroupService.getSiloGroup(vm.siloGroupId).then(
                function success(siloGroup) {
                    vm.siloGroup = siloGroup;
                    loadTemperaturesThresholds();
                },
                function fail() {
                    vm.loading = false;
                }
            );
        }
    });

    function loadTemperaturesThresholds() {
        siloGroupService.getSiloGroupSilos(vm.siloGroupId).then(
            (silos) => {
                vm.maxSiloGroupTemperature = null;
                vm.maxSiloGroupDeltaTemperature = null;
                silos.forEach((silo) => {
                    if (angular.isDefined(silo.maxTemperature) && silo.maxTemperature != null) {
                        if (vm.maxSiloGroupTemperature == null) {
                            vm.maxSiloGroupTemperature = silo.maxTemperature;
                        } else {
                            vm.maxSiloGroupTemperature = Math.min(vm.maxSiloGroupTemperature, silo.maxTemperature);
                        }
                    }
                    if (angular.isDefined(silo.maxDeltaTemperature) && silo.maxDeltaTemperature != null) {
                        if (vm.maxSiloGroupDeltaTemperature == null) {
                            vm.maxSiloGroupDeltaTemperature = silo.maxDeltaTemperature;
                        } else {
                            vm.maxSiloGroupDeltaTemperature = Math.min(vm.maxSiloGroupDeltaTemperature, silo.maxDeltaTemperature);
                        }
                    }
                });
                vm.maxSiloGroupTemperature = vm.maxSiloGroupTemperature || 24;
                vm.maxSiloGroupDeltaTemperature = vm.maxSiloGroupDeltaTemperature || 1;
                updateSubscriptions();
            },
            () => {
                vm.loading = false;
            }
        );
    }

    function updateSubscriptions() {
        var latestValuesSubscriptionsInfo = [{
            type: types.datasourceType.entity,
            entityType: types.entityType.asset,
            entityId: vm.siloGroup.id.id,
            entityName: vm.siloGroup.name,
            timeseries: [
                {name: vm.agTypes.telemetry.siloGroup.currentMaxTemperature},
                {name: vm.agTypes.telemetry.siloGroup.currentMaxDeltaTemperature}
            ]
        }];
        vm.latestValuesSubscriptionOptions.timeWindowConfig = angular.copy(vm.ctx.dashboardTimewindow);
        vm.latestValuesSubscriptionOptions.timeWindowConfig.aggregation.type = types.aggregation.none.value;
        vm.latestValuesSubscriptionOptions.timeWindowConfig.aggregation.limit = 1;
        vm.ctx.subscriptionApi.createSubscriptionFromInfo (
            types.widgetType.timeseries.value, latestValuesSubscriptionsInfo, vm.latestValuesSubscriptionOptions, false, true).then(
            function(subscription) {
                vm.latestValuesSubscription = subscription;
            }
        );
        vm.siloGroupMaxTempHistorySubscriptionsInfo = [{
            type: types.datasourceType.entity,
            entityType: types.entityType.asset,
            entityId: vm.siloGroup.id.id,
            entityName: vm.siloGroup.name,
            timeseries: [
                {
                    name: agTypes.telemetry.siloGroup.currentMaxTemperature,
                    label: $translate.instant('ag.silo.max-temperature'),
                    color: agTypes.colors.maxColor,
                    postFuncBody: unitService.temperaturePostprocessingFuncBody()
                }
            ]
        }];
    }

    function tempColor(val) {
        if (val >= vm.maxSiloGroupTemperature) {
            return agTypes.colors.errorColor;
        } else {
            return agTypes.colors.normalGreyColor;
        }
    }

    function deltaTempColor(val) {
        if (val >= vm.maxSiloGroupDeltaTemperature) {
            return agTypes.colors.errorColor;
        } else {
            return agTypes.colors.normalGreyColor;
        }
    }

    $scope.$on('dashboardTimewindowChanged', function (event, newDashboardTimewindow) {
        if (vm.latestValuesSubscription) {
            var newTimewindowConfig = angular.copy(newDashboardTimewindow);
            newTimewindowConfig.aggregation.type = types.aggregation.none.value;
            newTimewindowConfig.aggregation.limit = 1;
            vm.latestValuesSubscription.timeWindowConfig = newTimewindowConfig;
        }
    });

    $scope.$on('$destroy', function() {
        reset();
    });

    function reset() {
        if (vm.latestValuesSubscription) {
            vm.ctx.subscriptionApi.removeSubscription(vm.latestValuesSubscription.id);
        }
        resetValues();
    }

    function resetValues() {
        vm.maxTempVal = 0;
        vm.maxDeltaTempVal = 0;
        vm.maxTemp = '';
        vm.maxDeltaTemp = '';
    }

    function onDataUpdated(subscription, apply) {
        var data = subscription.data;
        for (var i=0;i<data.length;i++) {
            var datasourceData = data[i];
            var val = getNumberValue(datasourceData);
            switch (datasourceData.dataKey.name) {
                case vm.agTypes.telemetry.siloGroup.currentMaxTemperature:
                    vm.maxTempVal = val;
                    vm.maxTemp = vm.ctx.utils.formatValue(unitService.toUserTemperature(val), 0, '')+'°';
                    break;
                case vm.agTypes.telemetry.siloGroup.currentMaxDeltaTemperature:
                    vm.maxDeltaTempVal = val;
                    vm.maxDeltaTemp = vm.ctx.utils.formatValue(unitService.toUserTemperature(val, true), 1, '')+'°';
                    break;
            }
        }
        if (apply) {
            $scope.$digest();
        }
    }

    function onDataUpdateError(subscription, e) {
        $log.error(e);
    }

    function getNumberValue(keyData) {
        if (keyData && keyData.data && keyData.data[0] && angular.isDefined(keyData.data[0][1])) {
            if (!isNaN(keyData.data[0][1])) {
                return Number(keyData.data[0][1]);
            } else {
                return 0;
            }
        } else {
            return 0;
        }
    }

}